import { ApiError } from '@echo/api-client/client';
import { useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { isRetryable } from '../content/errors';
import {
  type AccountPreferences,
  exerciseMinutesOf,
  loadPreferences,
  savePreferences,
} from '../content/preferences';
import { retryDelayMs } from '../domain/retry';
import { queryKeys } from '../query/keys';
import {
  adoptExerciseMinutes,
  exerciseMinutesGeneration,
  exerciseMinutesRefused,
  exerciseMinutesSaved,
  heldExerciseMinutes,
  onExerciseMinutesChosen,
  retryExerciseMinutes,
  unsavedExerciseMinutes,
  useRefusedExerciseMinutes,
} from './exercise-minutes-store';
import { useConfig, useSession } from './provider';
import { useMe } from './use-me';

const SAVE_KEY = ['preferences', 'save'] as const;

/** A save is for the session it was queued in; the person has since signed out. */
class SessionEndedError extends Error {
  readonly sessionEnded = true;
}
const endedSession = (error: unknown) => error instanceof SessionEndedError;

interface Save {
  minutes: number;
  generation: number;
}

const saveOf = (minutes: number): Save => ({ minutes, generation: exerciseMinutesGeneration() });

/**
 * Account › Exercise duration on the person's account, so it follows them from device to device
 * (PRD-2450). What Core holds replaces the device's value each time it is read; each duration
 * the person chooses is written back, and an account with no duration yet gets the one this
 * device already holds. A save that fails (a network failure the device did not see coming,
 * Core in trouble, or another device saved first) is tried again on the quiet-retry schedule
 * (`retryDelayMs`) against the version Core holds then; until it lands, a read does not replace
 * the choice. While the device is offline the save waits and goes when it is back. A choice not
 * yet on the account is kept on the device and sent again when the app next starts. A save Core
 * refuses outright gives the choice up until the person retries it
 * (`useExerciseMinutesSaveState`). Mount it once at each app's root, after the session gate.
 * Where the environment has no `preferences` type the duration stays on the device.
 */
export function useExerciseMinutesSync(): void {
  const session = useSession();
  const preferencesType = useConfig().strongerMemoryTypes?.preferences;
  const personUuid = useMe().data?.uuid;
  const queryClient = useQueryClient();
  const key = queryKeys.preferences();
  const ready =
    session.status === 'authenticated' && preferencesType !== undefined && personUuid !== undefined;
  const load = () => loadPreferences(preferencesType as string, personUuid as string);

  // One save at a time, each editing the version Core holds now: another device may have
  // saved since the last read.
  const { mutate } = useMutation({
    mutationKey: SAVE_KEY,
    scope: { id: 'account-preferences' },
    // Waits out an offline spell instead of failing, and goes when the device is back online.
    networkMode: 'online',
    mutationFn: async ({ minutes, generation }: Save) => {
      const current = await load();
      if (generation !== exerciseMinutesGeneration()) throw new SessionEndedError();
      return savePreferences(preferencesType as string, current, { exerciseMinutes: minutes });
    },
    // A 409 is a version another device replaced; the retry edits the one after it.
    retry: (_failures, error) =>
      !endedSession(error) &&
      (isRetryable(error) || (error instanceof ApiError && error.status === 409)),
    retryDelay: retryDelayMs,
    onMutate: () => queryClient.cancelQueries({ queryKey: key }),
    onSuccess: (saved, { minutes, generation }) => {
      if (generation !== exerciseMinutesGeneration()) return;
      exerciseMinutesSaved(minutes);
      queryClient.setQueryData<AccountPreferences>(key, saved);
    },
    onError: (error, { minutes, generation }) => {
      if (!endedSession(error) && generation === exerciseMinutesGeneration())
        exerciseMinutesRefused(minutes);
    },
  });

  useQuery({
    queryKey: key,
    enabled: ready,
    staleTime: 60_000,
    queryFn: async ({ signal }) => {
      const preferences = await load();
      // A read that started before a save (cancelled when it began) must not undo the choice.
      const saving = queryClient.isMutating({ mutationKey: SAVE_KEY }) > 0;
      if (signal.aborted || saving) return preferences;
      const minutes = exerciseMinutesOf(preferences);
      if (minutes !== undefined) {
        adoptExerciseMinutes(minutes);
        return preferences;
      }
      // Queued with the person's choices, so one made meanwhile edits the node this makes.
      const held = heldExerciseMinutes();
      if (held !== undefined) mutate(saveOf(held));
      return preferences;
    },
  });

  useEffect(() => {
    if (!ready) return;
    // A choice the app closed before saving; not queued twice when this runs again mid-save.
    const unsaved = unsavedExerciseMinutes();
    if (unsaved !== undefined && queryClient.isMutating({ mutationKey: SAVE_KEY }) === 0)
      mutate(saveOf(unsaved));
    return onExerciseMinutesChosen((minutes) => mutate(saveOf(minutes)));
  }, [ready, mutate, queryClient]);
}

/**
 * Whether the duration the person chose is on their account yet. `pending`: a save is waiting
 * for the device to be back online; a save that simply takes a moment, or is being tried again
 * while online, is not pending. `refused`: Core turned the choice down; `retry` sends it again.
 */
export function useExerciseMinutesSaveState(): {
  pending: boolean;
  refused: boolean;
  retry: () => void;
} {
  const waiting = useMutationState({
    filters: { mutationKey: SAVE_KEY, status: 'pending' },
    select: (mutation) => mutation.state.isPaused,
  });
  const refused = useRefusedExerciseMinutes() !== undefined;
  return { pending: waiting.includes(true), refused, retry: retryExerciseMinutes };
}
